import { ChevronDown, ChevronRight, MessageSquareText, Plus, Trash2 } from "lucide-react";
import { ChangeEvent, Dispatch, memo, ReactNode, useMemo, useState } from "react";

import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { cn } from "@/lib/utils";
import { safeCalcItem, safeCalcTrade } from "@buildpy/core/modules/quote/safe-calc";
import { MONEY_MAX } from "@buildpy/core/modules/quote/schema";
import { formatWon } from "@buildpy/core/lib/format";
import Money from "@/components/money";
import { formatQty, formatAmount } from "@/modules/quote/ui/format";
import TemplatePicker from "@/modules/quote/ui/editor/template-picker";
import { fieldsetFreePress, lockedNow, openUnlessLocked, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";
import { qtyDisplayOf } from "@buildpy/core/modules/quote/editor/sheet/cell-edit";
import {
  cellText,
  isCellEditable,
  profitRatePctText,
  SheetColumn,
  SheetDataRow,
} from "@buildpy/core/modules/quote/editor/sheet/grid-model";
import { EditorAction, EditorItem, EditorTrade } from "@buildpy/core/modules/quote/editor/types";
import { TemplateItem } from "@buildpy/core/modules/template/types";
import { calcSaleOf, isSaleOverridden, itemMarginOf } from "@buildpy/core/modules/quote/editor/reducer";
import type { VatMode } from "@buildpy/core/modules/money/vat";

const CELL =
  "h-8 overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-border px-1.5 text-sm";

const PRICE_KEYS = new Set([
  "material_unit_price",
  "labor_unit_price",
  "simple_price",
  "cost_unit_price",
]);

interface ItemRowProps {
  item: EditorItem;
  kind: "item" | "child";
  rowIndex: number;
  /** 화면의 항목 번호(1부터). */
  no: number;
  columns: SheetColumn[];
  /** 이 행에 걸친 선택 범위의 열 — 없으면 -1. */
  selLeft: number;
  selRight: number;
  /** 커서가 이 행에 있으면 그 열, 아니면 -1. */
  cursorCol: number;
  area: number | null;
  areaM2?: number | null;
  spaces: { code: string; label: string }[];
  vatMode?: VatMode;
}

// 칸 하나의 글자·강조. 셀 이벤트는 표가 data-r/data-c 로 한꺼번에 받는다.
const SheetItemRowBase = ({
  item,
  kind,
  rowIndex,
  no,
  columns,
  selLeft,
  selRight,
  cursorCol,
  area,
  areaM2,
  spaces,
  vatMode = "INCL",
}: ItemRowProps) => {
  const row = useMemo<SheetDataRow>(
    () => ({ kind, ref: { tradeKey: "", itemKey: item.key, parentKey: null }, item }),
    [kind, item]
  );
  const amount = useMemo(() => safeCalcItem(item).amount, [item]);
  const isSet = item.children.length > 0;
  const qty = useMemo(() => qtyDisplayOf(item, area, areaM2), [item, area, areaM2]);
  const margin = useMemo(() => itemMarginOf(item, vatMode), [item, vatMode]);
  // 판매가를 직접 고친 줄의 계산값 — 마진칸에 표시하고 되돌리기는 도구 막대에서.
  const overrideCalc = useMemo(
    () => (isSaleOverridden(item, vatMode) ? calcSaleOf(item, vatMode) : null),
    [item, vatMode]
  );

  return (
    <tr data-r={rowIndex}>
      <th
        className={cn(
          "sticky left-0 z-[1] h-8 cursor-default select-none border-b border-r border-border bg-muted px-1 text-right text-xs font-normal tabular-nums text-muted-foreground",
          (selLeft >= 0 || cursorCol >= 0) && "bg-primary/15 text-foreground"
        )}
        data-rowhead=""
        scope="row">
        {no}
      </th>
      {columns.map((column, c) => {
        const editable = isCellEditable(row, column);
        const selected = selLeft >= 0 && c >= selLeft && c <= selRight;
        const qtyError = column.key === "qty" && !isSet && qty.kind === "error";
        const priceError =
          PRICE_KEYS.has(column.key) && editable && Number(cellText(row, column)) > MONEY_MAX;
        let content: ReactNode;
        if (column.key === "amount") {
          content = <Money value={amount} />;
        } else if (column.key === "qty") {
          if (isSet) content = "";
          else if (qty.kind === "expr")
            content = (
              <>
                <span className="mr-1 text-3xs italic text-muted-foreground">ƒ</span>
                {formatQty(qty.value)}
              </>
            );
          else if (qty.kind === "error")
            content = item.qty_expr ? `ƒ ${item.qty_expr}` : formatQty(item.qty);
          else content = formatQty(item.qty);
        } else if (PRICE_KEYS.has(column.key)) {
          const text = cellText(row, column);
          content = text === "" ? "" : formatAmount(Number(text));
        } else if (column.key === "margin") {
          content =
            margin === null ? (
              ""
            ) : (
              <>
                {overrideCalc !== null && (
                  <span className="mr-1 text-xs text-warning" data-sale-override="">
                    직접
                  </span>
                )}
                {`${cellText(row, column, { vatMode })}%`}
              </>
            );
        } else if (column.key === "item") {
          content = (
            <>
              {kind === "child" && <span className="mr-1 pl-3 text-muted-foreground">└</span>}
              {item.item}
              {isSet && (
                <span className="ml-1 rounded-md bg-muted px-1 text-3xs text-muted-foreground">
                  SET
                </span>
              )}
            </>
          );
        } else {
          content = cellText(row, column, { spaces });
        }

        return (
          <td
            key={column.key}
            className={cn(
              CELL,
              column.numeric && "text-right tabular-nums",
              !editable && "bg-muted/40 text-muted-foreground",
              column.key === "amount" && "font-medium text-foreground",
              PRICE_KEYS.has(column.key) &&
                column.key !== "cost_unit_price" &&
                item.suggested &&
                "text-status-new",
              (qtyError || priceError) && "bg-destructive/10 text-destructive",
              selected && "bg-primary/10",
              c === cursorCol && "outline outline-2 -outline-offset-2 outline-primary"
            )}
            data-c={c}
            data-cell={`${rowIndex}:${c}`}
            title={
              qtyError && qty.kind === "error"
                ? qty.message
                : priceError
                  ? "금액이 너무 큽니다 — 자릿수를 확인해 주세요"
                  : column.key === "margin" && margin !== null
                    ? `이익률 ${profitRatePctText(margin)}%${overrideCalc !== null ? ` · 판매가 직접 입력 · 계산값 ${formatWon(overrideCalc)}` : ""}`
                    : undefined
            }>
            {content}
          </td>
        );
      })}
    </tr>
  );
};

export const SheetItemRow = memo(SheetItemRowBase);

interface TradeRowProps {
  trade: EditorTrade;
  rowIndex: number;
  /** 행 번호 칸 + 금액 열 앞까지의 칸 수. */
  leadSpan: number;
  /** 금액 열 뒤의 칸 수. */
  tailSpan: number;
  dispatch: Dispatch<EditorAction>;
  templateItems: TemplateItem[];
  // 잠긴 견적 — 템플릿·조건 창을 닫고 머리 줄 단추·입력과 「추가」·조건 입력을 끈다.
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 재렌더 전에 들어온 조건 입력·템플릿 「추가」 도 버린다.
  isLocked: () => boolean;
}

// 공종 머리 줄 — 접기·이름·줄 추가·템플릿·조건 문구·삭제와 소계.
const SheetTradeRowBase = ({
  trade,
  rowIndex,
  leadSpan,
  tailSpan,
  dispatch,
  templateItems,
  disabled = false,
  isLocked,
}: TradeRowProps) => {
  const amounts = useMemo(
    () => safeCalcTrade({ group: trade.group, items: trade.items }),
    [trade.group, trade.items]
  );
  const [conditionOpen, setConditionOpen] = useState(false);
  const collapsed = trade.collapsed;

  return (
    <tr className="bg-card" data-trade-row={rowIndex}>
      <td className="border-b border-r border-border px-1.5 py-1" colSpan={leadSpan}>
        <div className="flex items-center gap-x-1.5">
          {/* 접기는 보기 전용 — 날 button 이 아닌 span 이라 잠긴 표 fieldset 안에서도 눌린다. */}
          <Button aria-expanded={!collapsed} aria-label={collapsed ? "펼치기" : "접기"} size="icon-xs" variant="ghost-muted" asChild>
            <span {...fieldsetFreePress(() => dispatch({ type: "TOGGLE_TRADE", tradeKey: trade.key }))}>
              {collapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
            </span>
          </Button>
          <Input
            aria-label="공종명"
            className="w-40 min-w-0 font-semibold"
            disabled={disabled}
            value={trade.name}
            variant="cell"
            onChange={(e) =>
              dispatch({ type: "SET_TRADE_NAME", tradeKey: trade.key, value: e.target.value })
            }
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {trade.items.length}줄
          </span>
          <Button
            disabled={disabled}
            size="compact"
            variant="ghost"
            onClick={() => dispatch({ type: "ADD_ITEM", tradeKey: trade.key })}>
            <Plus className="size-4" /> 줄
          </Button>
          <TemplatePicker
            disabled={disabled}
            isLocked={isLocked}
            items={templateItems}
            tradeName={trade.name}
            onAdd={(items) => dispatch({ type: "ADD_TEMPLATE_ITEMS", tradeKey: trade.key, items })}
          />
          <Dialog open={openUnlessLocked(conditionOpen, disabled)} onOpenChange={setConditionOpen}>
            <DialogTrigger asChild disabled={disabled}>
              <Button size="compact" variant="ghost">
                <MessageSquareText className="size-4" />
                조건
                {trade.condition_text.trim() !== "" && (
                  <>
                    <span aria-hidden className="size-1.5 rounded-full bg-primary" />
                    <span className="sr-only">(조건 문구 있음)</span>
                  </>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{trade.name} — 조건 문구</DialogTitle>
                <DialogDescription>견적서의 이 공종 아래에 그대로 찍힙니다.</DialogDescription>
              </DialogHeader>
              <Textarea
                aria-label={`${trade.name} 조건 문구`}
                className="min-h-32 text-sm"
                disabled={disabled}
                value={trade.condition_text}
                onChange={unlessLocked(
                  lockedNow(disabled, isLocked),
                  (e: ChangeEvent<HTMLTextAreaElement>) =>
                    dispatch({
                      type: "SET_TRADE_CONDITION",
                      tradeKey: trade.key,
                      value: e.target.value,
                    })
                )}
              />
            </DialogContent>
          </Dialog>
          <Button
            aria-label="공종 삭제"
            className="ml-auto"
            disabled={disabled}
            size="icon-xs"
            type="button"
            variant="ghost-destructive"
            onClick={() => dispatch({ type: "REMOVE_TRADE", tradeKey: trade.key })}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      </td>
      <td
        className="whitespace-nowrap border-b border-r border-border px-1.5 text-right text-sm font-semibold tabular-nums"
        title={`자재 ${formatWon(amounts.material_amount)} · 노무 ${formatWon(amounts.labor_amount)}`}>
        <Money value={amounts.amount} />
      </td>
      {tailSpan > 0 && <td className="border-b border-r border-border" colSpan={tailSpan} />}
    </tr>
  );
};

export const SheetTradeRow = memo(SheetTradeRowBase);
